import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Edit, Trash2, Eye, User, Users, Building2, CreditCard,
  Smartphone, Copy, Phone, Mail, MapPin, FileText, Calendar, ShieldCheck,
  ChevronDown, ChevronUp, CheckCircle, XCircle, AlertTriangle, AlertCircle
} from 'lucide-react';
import { proveedoresService, islrConceptosService } from '../../../services/api';

const FeedbackModal = ({ isOpen, type, title, message, onConfirm, onClose, showCancel }) => {
  if (!isOpen) return null;

  const icons = {
    success: <CheckCircle className="text-green-500 w-12 h-12" />,
    error: <XCircle className="text-red-500 w-12 h-12" />,
    warning: <AlertTriangle className="text-yellow-500 w-12 h-12" />,
    info: <AlertCircle className="text-blue-500 w-12 h-12" />
  };

  const colors = {
    success: 'bg-green-600 hover:bg-green-700',
    error: 'bg-red-600 hover:bg-red-700',
    warning: 'bg-yellow-600 hover:bg-yellow-700',
    info: 'bg-blue-600 hover:bg-blue-700'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 text-center transform transition-all">
        <div className="flex justify-center mb-4">
          {icons[type] || icons.info}
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="flex justify-center gap-3">
          {showCancel && (
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
          )}
          <button
            onClick={() => {
              if (onConfirm) onConfirm();
              onClose();
            }}
            className={`px-4 py-2 text-white rounded-lg transition-colors ${colors[type] || colors.info}`}
          >
            Aceptar
          </button>
        </div>
      </div>
    </div>
  );
};

const ProveedoresView = () => {
    const [proveedores, setProveedores] = useState([]);
    const [proveedoresFiltrados, setProveedoresFiltrados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
    const [stats, setStats] = useState(null);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [tipoFiltro, setTipoFiltro] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [expandedCards, setExpandedCards] = useState({});
    const sortColumn = 'nombre';
    const sortDirection = 'asc';
    const [feedback, setFeedback] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null, showCancel: false });
  
    const showFeedback = useCallback((type, title, message, onConfirm = null, showCancel = false) => {
      setFeedback({ isOpen: true, type, title, message, onConfirm, showCancel });
    }, []);
  
    const closeFeedback = useCallback(() => setFeedback(prev => ({ ...prev, isOpen: false })), []);

    const cargarProveedores = useCallback(async () => {
      try {
        setLoading(true);
        const response = await proveedoresService.getAll({});
        setProveedores(response.data);
        setProveedoresFiltrados(response.data);
      } catch (error) {
        console.error('Error al cargar proveedores:', error);
        showFeedback('error', 'Error', error.message || 'Error al cargar proveedores');
      } finally {
        setLoading(false);
      }
    }, [showFeedback]);

    const cargarStats = useCallback(async () => {
      try {
        const response = await proveedoresService.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Error al cargar estadísticas:', error);
      }
    }, []);

    useEffect(() => {
      cargarProveedores();
      cargarStats();
    }, [cargarProveedores, cargarStats]);

    useEffect(() => {
      let resultado = [...proveedores];

      if (tipoFiltro) {
        resultado = resultado.filter(p => p.tipo_persona === tipoFiltro);
      }

      if (searchTerm) {
        resultado = resultado.filter(p =>
          p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (p.cedula && p.cedula.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (p.rif && p.rif.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (p.email && p.email.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      resultado.sort((a, b) => {
        let aVal = a[sortColumn] || '';
        let bVal = b[sortColumn] || '';
        
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();

        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      setProveedoresFiltrados(resultado);
      setCurrentPage(1);
    }, [searchTerm, tipoFiltro, proveedores, sortColumn, sortDirection]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = proveedoresFiltrados.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(proveedoresFiltrados.length / itemsPerPage);

    const handleNuevoProveedor = () => {
      setModalMode('create');
      setProveedorSeleccionado(null);
      setShowModal(true);
    };

    const handleEditarProveedor = (proveedor) => {
      setModalMode('edit');
      setProveedorSeleccionado(proveedor);
      setShowModal(true);
    };

    const handleEliminarProveedor = (id) => {
      showFeedback('warning', 'Confirmar Eliminación', '¿Estás seguro de eliminar este proveedor?', async () => {
        try {
          await proveedoresService.delete(id);
          showFeedback('success', 'Éxito', 'Proveedor eliminado exitosamente');
          cargarProveedores();
          cargarStats();
        } catch (error) {
          showFeedback('error', 'Error', error.message || 'Error al eliminar proveedor');
        }
      }, true);
    };

    const handleVerProveedor = (proveedor) => {
      setModalMode('view');
      setProveedorSeleccionado(proveedor);
      setShowModal(true);
    };

    const toggleCardExpansion = (proveedorId) => {
      setExpandedCards(prev => ({
        ...prev,
        [proveedorId]: !prev[proveedorId]
      }));
    };

    return (
      <div>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-xl lg:text-2xl font-bold text-gray-800">Gestión de Proveedores</h2>
            <p className="text-sm lg:text-base text-gray-600">Administra tus proveedores y sus métodos de pago</p>
          </div>
          <button 
            onClick={handleNuevoProveedor}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 w-full sm:w-auto"
          >
            <Plus size={20} /> Nuevo Proveedor
          </button>
        </div>

        {/* Estadísticas */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-xs lg:text-sm">Total Proveedores</p>
                  <p className="text-xl lg:text-2xl font-bold text-gray-800">{stats.total}</p>
                </div>
                <Building2 className="text-blue-500" size={28} />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-xs lg:text-sm">Personas Naturales</p>
                  <p className="text-xl lg:text-2xl font-bold text-gray-800">{stats.naturales}</p>
                </div>
                <Users className="text-green-500" size={28} />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-xs lg:text-sm">Personas Jurídicas</p>
                  <p className="text-xl lg:text-2xl font-bold text-gray-800">{stats.juridicas}</p>
                </div>
                <Building2 className="text-purple-500" size={28} />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-xs lg:text-sm">Activos</p>
                  <p className="text-xl lg:text-2xl font-bold text-gray-800">{stats.activos}</p>
                </div>
                <CheckCircle className="text-green-500" size={28} />
              </div>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow mb-4 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <input 
              type="text" 
              placeholder="Buscar por nombre, cédula, RIF, email..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <select 
              value={tipoFiltro}
              onChange={(e) => setTipoFiltro(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Todos los tipos</option>
              <option value="natural">Persona Natural</option>
              <option value="juridica">Persona Jurídica</option>
            </select>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value={10}>10 por página</option>
              <option value={25}>25 por página</option>
              <option value={50}>50 por página</option>
              <option value={100}>100 por página</option>
            </select>
          </div>
        </div>

        {/* Lista de proveedores en tarjetas */}
        <div className="space-y-4">
          {loading ? (
            <div className="p-12 text-center">
              <p className="text-gray-600">Cargando proveedores...</p>
            </div>
          ) : proveedoresFiltrados.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 size={64} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No se encontraron proveedores</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {currentItems.map(proveedor => {
                const isExpanded = expandedCards[proveedor.id];

                return (
                  <div key={proveedor.id} className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                          {proveedor.tipo_persona === 'natural' ? <User size={20} /> : <Building2 size={20} />}
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-lg font-semibold text-slate-900 truncate">{proveedor.nombre}</h3>
                          <p className="text-sm text-slate-500 mt-1 truncate">
                            {proveedor.tipo_persona === 'natural' ? `Cédula: ${proveedor.cedula || '-'}` : `RIF: ${proveedor.rif || '-'}`}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 mt-3">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                              proveedor.tipo_persona === 'natural'
                                ? 'bg-indigo-100 text-indigo-700'
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {proveedor.tipo_persona === 'natural' ? 'Natural' : 'Jurídica'}
                            </span>
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                              proveedor.activo
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-rose-100 text-rose-700'
                            }`}>
                              {proveedor.activo ? 'Activo' : 'Inactivo'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 self-start lg:self-auto">
                        <button
                          onClick={() => toggleCardExpansion(proveedor.id)}
                          className="p-2 rounded-lg text-slate-700 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                          aria-expanded={isExpanded}
                          title={isExpanded ? 'Ocultar detalles' : 'Mostrar detalles'}
                        >
                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                        <button
                          onClick={() => handleVerProveedor(proveedor)}
                          className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 p-2 rounded-lg transition-colors"
                          title="Ver detalles"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleEditarProveedor(proveedor)}
                          className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 p-2 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleEliminarProveedor(proveedor.id)}
                          className="text-rose-600 hover:text-rose-800 hover:bg-rose-100 p-2 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    <div className={`${isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'} overflow-hidden transition-all duration-300`}>
                      <div className="px-6 pb-5 pt-4 border-t border-slate-200">
                        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                          <div className="rounded-3xl bg-slate-50 p-4 flex gap-3 items-start">
                            <Phone className="text-slate-400 mt-1" size={18} />
                            <div>
                              <p className="text-xs uppercase tracking-wide text-slate-500">Teléfono</p>
                              <p className="text-sm font-semibold text-slate-900 mt-2">{proveedor.telefono || '-'}</p>
                            </div>
                          </div>
                          <div className="rounded-3xl bg-slate-50 p-4 flex gap-3 items-start">
                            <Mail className="text-slate-400 mt-1" size={18} />
                            <div>
                              <p className="text-xs uppercase tracking-wide text-slate-500">Email</p>
                              <p className="text-sm font-semibold text-slate-900 mt-2 break-words">{proveedor.email || '-'}</p>
                            </div>
                          </div>
                          <div className="rounded-3xl bg-slate-50 p-4 flex gap-3 items-start">
                            <ShieldCheck className="text-slate-400 mt-1" size={18} />
                            <div>
                              <p className="text-xs uppercase tracking-wide text-slate-500">Agente Retención</p>
                              <p className={`text-sm font-semibold mt-2 ${proveedor.agente_retencion ? 'text-emerald-700' : 'text-slate-900'}`}>
                                {proveedor.agente_retencion ? `Sí (${(proveedor.porcentaje_retencion || 0.75) * 100}%)` : 'No'}
                              </p>
                            </div>
                          </div>
                          <div className="rounded-3xl bg-slate-50 p-4 flex gap-3 items-start">
                            <MapPin className="text-slate-400 mt-1" size={18} />
                            <div>
                              <p className="text-xs uppercase tracking-wide text-slate-500">Dirección</p>
                              <p className="text-sm font-semibold text-slate-900 mt-2">{proveedor.direccion || '-'}</p>
                            </div>
                          </div>
                        </div>
                        <div className="grid gap-3 mt-3 sm:grid-cols-2 xl:grid-cols-4">
                          <div className="rounded-3xl bg-slate-50 p-4 flex gap-3 items-start">
                            <FileText className="text-slate-400 mt-1" size={18} />
                            <div>
                              <p className="text-xs uppercase tracking-wide text-slate-500">Identificación</p>
                              <p className="text-sm font-semibold text-slate-900 mt-2">
                                {proveedor.tipo_persona === 'natural' ? (proveedor.cedula || '-') : (proveedor.rif || '-')}
                              </p>
                            </div>
                          </div>
                          <div className="rounded-3xl bg-slate-50 p-4 flex gap-3 items-start">
                            <Calendar className="text-slate-400 mt-1" size={18} />
                            <div>
                              <p className="text-xs uppercase tracking-wide text-slate-500">Registro</p>
                              <p className="text-sm font-semibold text-slate-900 mt-2">
                                {proveedor.fecha_registro || proveedor.created_at || '-'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {proveedoresFiltrados.length > 0 && (
            <div className="bg-white rounded-lg shadow mt-4 p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-sm text-gray-700 text-center sm:text-left">
                  Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, proveedoresFiltrados.length)} de {proveedoresFiltrados.length} proveedores
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Primero
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Anterior
                  </button>
                  <span className="px-3 py-1 text-sm">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Siguiente
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Último
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <ProveedorModal
            mode={modalMode}
            proveedor={proveedorSeleccionado}
            showFeedback={showFeedback}
            onClose={() => {
              setShowModal(false);
              setProveedorSeleccionado(null);
            }}
          onSuccess={(mensaje) => {
              setShowModal(false);
              setProveedorSeleccionado(null);
              cargarProveedores();
              cargarStats();
              if (mensaje) {
                showFeedback('success', 'Éxito', mensaje);
              }
            }}
            showFeedback={showFeedback}
          />
        )}
  
        <FeedbackModal
          {...feedback}
          onClose={closeFeedback}
        />
      </div>
    );
  };

  // Modal de Proveedor con gestión de métodos de pago -- exportado para
  // reusarlo desde Cotizaciones (registrar/editar proveedor sin salir
  // del flujo de cotizar un articulo). showFeedback es opcional: quien lo
  // reuse desde otro módulo puede no pasarlo y se cae a alert().
  export const ProveedorModal = ({ mode, proveedor, onClose, onSuccess, showFeedback }) => {
    const [formData, setFormData] = useState({
      tipo_persona: 'juridica',
      nombre: '',
      cedula: '',
      rif: '',
      telefono: '',
      email: '',
      direccion: '',
      notas: '',
      activo: true,
      agente_retencion: false,
      porcentaje_retencion: 0.75,
      islr_concepto_id: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('datos'); // datos, cuentas, pagos
    const [proveedorData, setProveedorData] = useState(null);
    const [bancos, setBancos] = useState([]);
    const [islrConceptos, setIslrConceptos] = useState([]);

    // Cargar datos del proveedor y bancos
    useEffect(() => {
      const cargarDatos = async () => {
        try {
          // Cargar lista de bancos y conceptos ISLR
          const [responseBancos, responseIslr] = await Promise.all([
            proveedoresService.getBancos(),
            islrConceptosService.getAll()
          ]);
          setBancos(responseBancos.data);
          setIslrConceptos(responseIslr.data || []);

          if (proveedor) {
            // Si es modo ver o editar, cargar datos completos
            if (mode === 'view' || mode === 'edit') {
              const response = await proveedoresService.getById(proveedor.id);
              setProveedorData(response.data);

              setFormData({
                tipo_persona: response.data.tipo_persona,
                nombre: response.data.nombre,
                cedula: response.data.cedula || '',
                rif: response.data.rif || '',
                telefono: response.data.telefono || '',
                email: response.data.email || '',
                direccion: response.data.direccion || '',
                notas: response.data.notas || '',
                activo: response.data.activo,
                agente_retencion: response.data.agente_retencion || false,
                porcentaje_retencion: response.data.porcentaje_retencion || 0.75,
                islr_concepto_id: response.data.islr_concepto_id || ''
              });
            } else {
              setFormData({
                tipo_persona: proveedor.tipo_persona,
                nombre: proveedor.nombre,
                cedula: proveedor.cedula || '',
                rif: proveedor.rif || '',
                telefono: proveedor.telefono || '',
                email: proveedor.email || '',
                direccion: proveedor.direccion || '',
                notas: proveedor.notas || '',
                activo: proveedor.activo,
                agente_retencion: proveedor.agente_retencion || false,
                porcentaje_retencion: proveedor.porcentaje_retencion || 0.75,
                islr_concepto_id: proveedor.islr_concepto_id || ''
              });
            }
          }
        } catch (error) {
          console.error('Error al cargar datos:', error);
        }
      };

      cargarDatos();
    }, [proveedor, mode]);

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');

      try {
        const payload = {
          ...formData,
          islr_concepto_id: formData.islr_concepto_id ? parseInt(formData.islr_concepto_id, 10) : null
        };
        let creado = null;
        let mensaje = '';
        if (mode === 'create') {
          const respuesta = await proveedoresService.create(payload);
          creado = respuesta.data;
          mensaje = 'Proveedor creado exitosamente';
        } else if (mode === 'edit') {
          await proveedoresService.update(proveedor.id, payload);
          mensaje = 'Proveedor actualizado exitosamente';
        }
        if (showFeedback) showFeedback('success', 'Éxito', mensaje);
        else alert(mensaje);
        // Segundo argumento (el proveedor recien creado) es nuevo: lo usa
        // quien abrio este modal desde otro flujo (ej. la edicion de un
        // item de cotizacion) para seleccionarlo automaticamente sin
        // recargar y buscarlo a mano. El primer argumento (mensaje) se
        // mantiene igual para no romper el uso original en este archivo.
        onSuccess(mensaje, creado);
      } catch (err) {
        setError(err.message || 'Error al guardar proveedor');
      } finally {
        setLoading(false);
      }
    };

    const isViewMode = mode === 'view';

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">
                {mode === 'create' && 'Nuevo Proveedor'}
                {mode === 'edit' && 'Editar Proveedor'}
                {mode === 'view' && 'Detalles del Proveedor'}
              </h3>
              <button 
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <span className="text-2xl">×</span>
              </button>
            </div>

            {/* Tabs */}
            {(mode === 'view' || mode === 'edit') && proveedorData && (
              <div className="flex gap-4 mt-4 border-b">
                <button
                  onClick={() => setActiveTab('datos')}
                  className={`pb-2 px-4 font-medium ${
                    activeTab === 'datos'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Datos Básicos
                </button>
                <button
                  onClick={() => setActiveTab('cuentas')}
                  className={`pb-2 px-4 font-medium flex items-center gap-2 ${
                    activeTab === 'cuentas'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <CreditCard size={18} />
                  Cuentas Bancarias ({proveedorData.cuentas_bancarias?.length || 0})
                </button>
                <button
                  onClick={() => setActiveTab('pagos')}
                  className={`pb-2 px-4 font-medium flex items-center gap-2 ${
                    activeTab === 'pagos'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Smartphone size={18} />
                  Pago Móvil ({proveedorData.pagos_movil?.length || 0})
                </button>
              </div>
            )}
          </div>

          <div className="p-6">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            {/* Tab: Datos Básicos */}
            {activeTab === 'datos' && (
              <DatosBasicosTab
                formData={formData}
                handleChange={handleChange}
                isViewMode={isViewMode}
                mode={mode}
                proveedorData={proveedorData}
                islrConceptos={islrConceptos}
              />
            )}

            {/* Tab: Cuentas Bancarias */}
            {activeTab === 'cuentas' && proveedorData && (
              <CuentasBancariasTab
                proveedor={proveedorData}
                bancos={bancos}
                isViewMode={isViewMode}
                showFeedback={showFeedback}
                onUpdate={async () => {
                  const response = await proveedoresService.getById(proveedorData.id);
                  setProveedorData(response.data);
                }}
              />
            )}

            {/* Tab: Pagos Móviles */}
            {activeTab === 'pagos' && proveedorData && (
              <PagosMovilTab
                proveedor={proveedorData}
                bancos={bancos}
                isViewMode={isViewMode}
                showFeedback={showFeedback}
                onUpdate={async () => {
                  const response = await proveedoresService.getById(proveedorData.id);
                  setProveedorData(response.data);
                }}
              />
            )}
          </div>

          <div className="p-6 border-t border-gray-200 flex justify-end gap-4 sticky bottom-0 bg-white">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {isViewMode ? 'Cerrar' : 'Cancelar'}
            </button>
            {!isViewMode && activeTab === 'datos' && (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`px-6 py-2 rounded-lg text-white ${
                  loading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Tab de Datos Básicos
  const DatosBasicosTab = ({ formData, handleChange, isViewMode, mode, proveedorData, islrConceptos }) => {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            Tipo de Persona <span className="text-red-500">*</span>
          </label>
          <select
            name="tipo_persona"
            value={formData.tipo_persona}
            onChange={handleChange}
            disabled={isViewMode}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          >
            <option value="natural">Persona Natural</option>
            <option value="juridica">Persona Jurídica</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              {formData.tipo_persona === 'natural' ? 'Nombre/Apellido' : 'Razón Social'} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              disabled={isViewMode}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder={formData.tipo_persona === 'natural' ? 'Ej: Yangel Romero' : 'Ej: Empresa ABC C.A.'}
            />
          </div>

       
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {formData.tipo_persona === 'natural' ? (
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Cédula <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="cedula"
                value={formData.cedula}
                onChange={handleChange}
                disabled={isViewMode}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Ej: V-12345678"
              />
            </div>
          ) : (
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                RIF <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="rif"
                value={formData.rif}
                onChange={handleChange}
                disabled={isViewMode}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Ej: J-123456789"
              />
            </div>
          )}

          <div>
            <label className="block text-gray-700 font-medium mb-2">Teléfono</label>
            <input
              type="text"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
              disabled={isViewMode}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="Ej: 414-1234567"
            />
          </div>
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-2">Correo Electrónico</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={isViewMode}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            placeholder="Ej: proveedor@email.com"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-2">Dirección</label>
          <textarea
            name="direccion"
            value={formData.direccion}
            onChange={handleChange}
            disabled={isViewMode}
            rows="3"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            placeholder="Dirección completa del proveedor"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-2">Notas</label>
          <textarea
            name="notas"
            value={formData.notas}
            onChange={handleChange}
            disabled={isViewMode}
            rows="3"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            placeholder="Notas adicionales sobre el proveedor"
          />
        </div>

        {mode === 'edit' && (
          <div className="flex items-center">
            <input
              type="checkbox"
              name="activo"
              checked={formData.activo}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label className="ml-2 text-gray-700 font-medium">Proveedor Activo</label>
          </div>
        )}

        {/* Agente de Retención */}
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <div className="flex items-center mb-3">
            <input
              type="checkbox"
              name="agente_retencion"
              checked={formData.agente_retencion}
              onChange={handleChange}
              disabled={isViewMode}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label className="ml-2 text-gray-800 font-semibold flex items-center gap-2">
              Es Agente de Retención
              <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded">IVA</span>
            </label>
          </div>

          {formData.agente_retencion && (
            <div className="ml-6 mt-3">
              <label className="block text-gray-700 font-medium mb-2">
                Porcentaje de Retención de IVA
              </label>
              <select
                name="porcentaje_retencion"
                value={formData.porcentaje_retencion}
                onChange={handleChange}
                disabled={isViewMode}
                className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value={0.75}>75% (Régimen General)</option>
                <option value={1}>100% (Retención Total)</option>
              </select>
              <p className="text-sm text-gray-500 mt-2">
                Este porcentaje se aplicará al cálculo de retenciones de IVA en el módulo de Recaudos.
              </p>
            </div>
          )}
        </div>

        {/* Concepto de retención de ISLR (independiente del IVA) */}
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <label className="block text-gray-800 font-semibold mb-2 flex items-center gap-2">
            Concepto ISLR
            <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded">ISLR</span>
          </label>
          <select
            name="islr_concepto_id"
            value={formData.islr_concepto_id}
            onChange={handleChange}
            disabled={isViewMode}
            className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          >
            <option value="">Sin concepto asignado</option>
            {islrConceptos.map(c => {
              const pct = (formData.tipo_persona === 'natural' ? c.porcentaje_pn : c.porcentaje_pj) || 0;
              return (
                <option key={c.id} value={c.id}>
                  {c.nombre} ({(pct * 100).toFixed(0)}%)
                </option>
              );
            })}
          </select>
          <p className="text-sm text-gray-500 mt-2">
            Necesario para retener ISLR al registrar facturas de compra de este proveedor. Sin esto, la retención se rechaza pidiendo el concepto a mano.
          </p>
        </div>

        {isViewMode && proveedorData && (
          <div className="border-t pt-4 mt-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Creado por:</p>
                <p className="font-semibold">{proveedorData.creador_nombre || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-600">Fecha de creación:</p>
                <p className="font-semibold">
                  {new Date(proveedorData.created_at).toLocaleDateString('es-ES')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

 // Tab de Cuentas Bancarias
  const CuentasBancariasTab = ({ proveedor, bancos, isViewMode, onUpdate, showFeedback }) => {
    const [showFormCuenta, setShowFormCuenta] = useState(false);
    const [cuentaEditando, setCuentaEditando] = useState(null);
    const [formCuenta, setFormCuenta] = useState({
      banco_codigo: '',
      numero_cuenta: '',
      tipo_cuenta: 'corriente',
      titular_cuenta: '',
      cedula_titular: '',
      principal: false
    });
    const [loading, setLoading] = useState(false);

    const resetForm = () => {
      setFormCuenta({
        banco_codigo: '',
        numero_cuenta: '',
        tipo_cuenta: 'corriente',
        titular_cuenta: '',
        cedula_titular: '',
        principal: false
      });
      setCuentaEditando(null);
      setShowFormCuenta(false);
    };

    const handleAddCuenta = () => {
      setFormCuenta({
        banco_codigo: '',
        numero_cuenta: '',
        tipo_cuenta: 'corriente',
        cedula_titular: proveedor.cedula || proveedor.rif || '',
        principal: proveedor.cuentas_bancarias.length === 0
      });
      setShowFormCuenta(true);
    };

    const handleEditCuenta = (cuenta) => {
      setFormCuenta({
        banco_codigo: cuenta.banco_codigo,
        numero_cuenta: cuenta.numero_cuenta,
        tipo_cuenta: cuenta.tipo_cuenta,
        titular_cuenta: cuenta.titular_cuenta,
        cedula_titular: cuenta.cedula_titular,
        principal: cuenta.principal
      });
      setCuentaEditando(cuenta);
      setShowFormCuenta(true);
    };

    const handleSubmitCuenta = async (e) => {
      e.preventDefault();
      setLoading(true);

      try {
        if (cuentaEditando) {
          await proveedoresService.updateCuentaBancaria(proveedor.id, cuentaEditando.id, formCuenta);
          showFeedback('success', 'Éxito', 'Cuenta bancaria actualizada exitosamente');
        } else {
          await proveedoresService.addCuentaBancaria(proveedor.id, formCuenta);
          showFeedback('success', 'Éxito', 'Cuenta bancaria agregada exitosamente');
        }
        await onUpdate();
        resetForm();
      } catch (error) {
        showFeedback('error', 'Error', error.message || 'Error al guardar cuenta bancaria');
      } finally {
        setLoading(false);
      }
    };

    const handleDeleteCuenta = async (cuentaId) => {
      showFeedback('warning', 'Eliminar cuenta', '¿Estás seguro de eliminar esta cuenta bancaria?', async () => {
        try {
          await proveedoresService.deleteCuentaBancaria(proveedor.id, cuentaId);
          showFeedback('success', 'Éxito', 'Cuenta bancaria eliminada exitosamente');
          await onUpdate();
        } catch (error) {
          showFeedback('error', 'Error', error.message || 'Error al eliminar cuenta bancaria');
        }
      }, true);
    };

    return (
      <div className="space-y-4">
        {!isViewMode && !showFormCuenta && (
          <button
            onClick={handleAddCuenta}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
          >
            <Plus size={18} /> Agregar Cuenta Bancaria
          </button>
        )}

        {showFormCuenta && (
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-300">
            <h4 className="font-semibold text-gray-800 mb-4">
              {cuentaEditando ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}
            </h4>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Banco <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formCuenta.banco_codigo}
                    onChange={(e) => setFormCuenta({...formCuenta, banco_codigo: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Seleccionar banco...</option>
                    {bancos.map(banco => (
                      <option key={banco.codigo} value={banco.codigo}>
                        {banco.codigo} - {banco.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Tipo de Cuenta <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formCuenta.tipo_cuenta}
                    onChange={(e) => setFormCuenta({...formCuenta, tipo_cuenta: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="corriente">Corriente</option>
                    <option value="ahorro">Ahorro</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Número de Cuenta <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formCuenta.numero_cuenta}
                  onChange={(e) => setFormCuenta({...formCuenta, numero_cuenta: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="20 dígitos"
                  maxLength="20"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Titular de la Cuenta <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formCuenta.titular_cuenta}
                    onChange={(e) => setFormCuenta({...formCuenta, titular_cuenta: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Cédula/RIF del Titular <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formCuenta.cedula_titular}
                    onChange={(e) => setFormCuenta({...formCuenta, cedula_titular: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formCuenta.principal}
                  onChange={(e) => setFormCuenta({...formCuenta, principal: e.target.checked})}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                />
                <label className="ml-2 text-gray-700 font-medium">Cuenta Principal</label>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleSubmitCuenta}
                  disabled={loading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                  onClick={resetForm}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Cuentas CON BOTÓN DE COPIAR */}
        {proveedor.cuentas_bancarias && proveedor.cuentas_bancarias.length > 0 ? (
          <div className="space-y-3">
            {proveedor.cuentas_bancarias.map(cuenta => {
              // Preparar texto para copiar
              const textoCuenta = `DATOS BANCARIOS
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  🏦 Banco: ${cuenta.banco_nombre}
  💳 Número de Cuenta: ${cuenta.numero_cuenta}
  📋 Tipo: ${cuenta.tipo_cuenta.charAt(0).toUpperCase() + cuenta.tipo_cuenta.slice(1)}
  👤 Titular: ${cuenta.titular_cuenta}
  🆔 Cédula/RIF: ${cuenta.cedula_titular}`.trim();

              return (
                <div key={cuenta.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="text-blue-600" size={20} />
                        <h5 className="font-semibold text-gray-800">
                          {cuenta.banco_nombre}
                        </h5>
                        {cuenta.principal && (
                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                            Principal
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-gray-600">Número de Cuenta:</p>
                          <p className="font-mono font-semibold">{cuenta.numero_cuenta}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Tipo:</p>
                          <p className="font-semibold capitalize">{cuenta.tipo_cuenta}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Titular:</p>
                          <p className="font-semibold">{cuenta.titular_cuenta}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Cédula/RIF:</p>
                          <p className="font-semibold">{cuenta.cedula_titular}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {/* Botón de copiar */}
                      <button
                        onClick={() => copiarAlPortapapeles(textoCuenta, 'Datos bancarios copiados', showFeedback)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Copiar datos bancarios"
                      >
                        <Copy size={18} />
                      </button>
                      {!isViewMode && (
                        <>
                          <button
                            onClick={() => handleEditCuenta(cuenta)}
                            className="text-green-600 hover:text-green-800"
                            title="Editar"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteCuenta(cuenta.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Eliminar"
                          >
                            <Trash2 size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <CreditCard size={48} className="mx-auto mb-2 text-gray-400" />
            <p>No hay cuentas bancarias registradas</p>
          </div>
        )}
      </div>
    );
  };
  // Tab de Pagos Móviles
  const PagosMovilTab = ({ proveedor, bancos, isViewMode, onUpdate, showFeedback }) => {
    const [showFormPago, setShowFormPago] = useState(false);
    const [pagoEditando, setPagoEditando] = useState(null);
    const [formPago, setFormPago] = useState({
      banco_codigo: '',
      telefono: '',
      cedula: '',
      titular: '',
      principal: false
    });
    const [loading, setLoading] = useState(false);

    const resetForm = () => {
      setFormPago({
        banco_codigo: '',
        telefono: '',
        cedula: '',
        titular: '',
        principal: false
      });
      setPagoEditando(null);
      setShowFormPago(false);
    };

    const handleAddPago = () => {
      setFormPago({
        banco_codigo: '',
        telefono: proveedor.telefono || '',
        cedula: proveedor.cedula || proveedor.rif || '',
        principal: proveedor.pagos_movil.length === 0
      });
      setShowFormPago(true);
    };

    const handleEditPago = (pago) => {
      setFormPago({
        banco_codigo: pago.banco_codigo,
        telefono: pago.telefono,
        cedula: pago.cedula,
        titular: pago.titular,
        principal: pago.principal
      });
      setPagoEditando(pago);
      setShowFormPago(true);
    };

    const handleSubmitPago = async (e) => {
      e.preventDefault();
      setLoading(true);

      try {
        if (pagoEditando) {
          await proveedoresService.updatePagoMovil(proveedor.id, pagoEditando.id, formPago);
          showFeedback('success', 'Éxito', 'Pago móvil actualizado exitosamente');
        } else {
          await proveedoresService.addPagoMovil(proveedor.id, formPago);
          showFeedback('success', 'Éxito', 'Pago móvil agregado exitosamente');
        }
        await onUpdate();
        resetForm();
      } catch (error) {
        showFeedback('error', 'Error', error.message || 'Error al guardar pago móvil');
      } finally {
        setLoading(false);
      }
    };

    const handleDeletePago = async (pagoId) => {
      showFeedback('warning', 'Eliminar pago móvil', '¿Estás seguro de eliminar este pago móvil?', async () => {
        try {
          await proveedoresService.deletePagoMovil(proveedor.id, pagoId);
          showFeedback('success', 'Éxito', 'Pago móvil eliminado exitosamente');
          await onUpdate();
        } catch (error) {
          showFeedback('error', 'Error', error.message || 'Error al eliminar pago móvil');
        }
      }, true);
    };

    return (
      <div className="space-y-4">
        {!isViewMode && !showFormPago && (
          <button
            onClick={handleAddPago}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
          >
            <Plus size={18} /> Agregar Pago Móvil
          </button>
        )}

        {showFormPago && (
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-300">
            <h4 className="font-semibold text-gray-800 mb-4">
              {pagoEditando ? 'Editar Pago Móvil' : 'Nuevo Pago Móvil'}
            </h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Banco <span className="text-red-500">*</span>
                </label>
                <select
                  value={formPago.banco_codigo}
                  onChange={(e) => setFormPago({...formPago, banco_codigo: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="">Seleccionar banco...</option>
                  {bancos.map(banco => (
                    <option key={banco.codigo} value={banco.codigo}>
                      {banco.codigo} - {banco.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Teléfono <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formPago.telefono}
                    onChange={(e) => setFormPago({...formPago, telefono: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Ej: 04141234567"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Cédula/RIF <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formPago.cedula}
                    onChange={(e) => setFormPago({...formPago, cedula: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Ej: V-12345678"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Titular <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formPago.titular}
                  onChange={(e) => setFormPago({...formPago, titular: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formPago.principal}
                  onChange={(e) => setFormPago({...formPago, principal: e.target.checked})}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                />
                <label className="ml-2 text-gray-700 font-medium">Pago Móvil Principal</label>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleSubmitPago}
                  disabled={loading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                  onClick={resetForm}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Pagos Móviles CON BOTÓN DE COPIAR */}
        {proveedor.pagos_movil && proveedor.pagos_movil.length > 0 ? (
          <div className="space-y-3">
            {proveedor.pagos_movil.map(pago => {
              // Preparar texto para copiar
              const textoPago = `DATOS PAGO MÓVIL
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  🏦 Banco: ${pago.banco_nombre}
  📱 Teléfono: ${pago.telefono}
  🆔 Cédula/RIF: ${pago.cedula}
  👤 Titular: ${pago.titular}`.trim();

              return (
                <div key={pago.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Smartphone className="text-green-600" size={20} />
                        <h5 className="font-semibold text-gray-800">
                          {pago.banco_nombre}
                        </h5>
                        {pago.principal && (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                            Principal
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-gray-600">Teléfono:</p>
                          <p className="font-mono font-semibold">{pago.telefono}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Cédula/RIF:</p>
                          <p className="font-semibold">{pago.cedula}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-600">Titular:</p>
                          <p className="font-semibold">{pago.titular}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {/* Botón de copiar */}
                      <button
                        onClick={() => copiarAlPortapapeles(textoPago, 'Datos de pago móvil copiados', showFeedback)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Copiar datos de pago móvil"
                      >
                        <Copy size={18} />
                      </button>
                      {!isViewMode && (
                        <>
                          <button
                            onClick={() => handleEditPago(pago)}
                            className="text-green-600 hover:text-green-800"
                            title="Editar"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDeletePago(pago.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Eliminar"
                          >
                            <Trash2 size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Smartphone size={48} className="mx-auto mb-2 text-gray-400" />
            <p>No hay pagos móviles registrados</p>
          </div>
        )}
      </div>
    );
  };
  const copiarAlPortapapeles = (texto, mensaje = 'Copiado al portapapeles', onFeedback) => {
    navigator.clipboard.writeText(texto).then(() => {
      if (onFeedback) onFeedback('success', 'Copiado', mensaje);
    }).catch(err => {
      console.error('Error al copiar:', err);
      if (onFeedback) onFeedback('error', 'Error', 'Error al copiar al portapapeles');
    });
  };


export default ProveedoresView;